import { createQueryKeys } from "@lukemorales/query-key-factory";
import { get, post } from "@/api/api";
import { useQuery } from "@tanstack/react-query";
import { RefreshInterval } from "@/dateRanges/refreshIntervals";

export interface QueryBoundsData {
  id: number;
  query_type: string;
  min_bound: number | null;
  max_bound: number | null;
  unit: string;
  created_at: string;
  updated_at: string;
}

export interface QueryTypeData {
  query_type: string;
  display_name: string;
  is_diagnostic: boolean;
  default_unit: string;
}

export const queryBounds = createQueryKeys('queryBounds', {
  detail: (queryType: string) => ({
    queryKey: [queryType],
    queryFn: async () => {
      return await get<QueryBoundsData>(`query-bounds/${queryType}/`);
    }
  }),
});

export const queryTypes = createQueryKeys('queryTypes', {
  detail: (queryType: string) => ({
    queryKey: [queryType],
    queryFn: async () => {
      return await get<QueryTypeData>(`query-types/${queryType}/`);
    }
  }),
});

export const useQueryBounds = (queryType: string) =>
  useQuery(queryBounds.detail(queryType));

export const useQueryType = (queryType: string) =>
  useQuery(queryTypes.detail(queryType));

export interface DataExistsInput {
  sensorKitSerialNumber: string;
  start: string;
  stop: string;
}

export interface DataExistsOutput {
  exists: boolean;
}

export const dataExists = createQueryKeys('dataExists', {
  check: (params: DataExistsInput) => ({
    queryKey: [params.sensorKitSerialNumber, params.start, params.stop],
    queryFn: async () => {
      return await post("queries/data-exists/", { node: params.sensorKitSerialNumber, ...params }) as DataExistsOutput;
    }
  }),
});

export const useDataExists = (params: DataExistsInput) =>
  useQuery(dataExists.check(params));

export interface MostRecentValueInput {
  query_type: string;
  node: string;
}

export interface PintQuantity {
  magnitude: number;
  units: string;
}

export interface MostRecentValueResponse {
  time: string | null;
  data: PintQuantity | null;
}

export const mostRecentValue = createQueryKeys('mostRecentValue', {
  get: (input: MostRecentValueInput) => ({
    queryKey: [input.query_type, input.node],
    queryFn: async () => {
      return await post('queries/most-recent-value/', input) as MostRecentValueResponse;
    },
  }),
});

export const useMostRecentValue = (
  input: MostRecentValueInput,
  refreshInterval?: RefreshInterval
) => {
  return useQuery({
    ...mostRecentValue.get(input),
    refetchInterval: () => {
      if (refreshInterval?.value !== 0) {
        return refreshInterval?.value;
      }
      return false;
    },
  });
};
